import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { PartnerDocument, ApiError } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';
import { Download, FileText, Loader2 } from 'lucide-react';

/** Point a pre-opened tab at the document (PDF) or trigger a named download (Word); release the object URL afterwards. */
function deliverDocument(tab: Window | null, url: string, fileName: string, mimeType: string): boolean {
  const isPdf = mimeType === 'application/pdf';
  if (isPdf && tab) {
    tab.location.href = url;
  } else {
    if (tab) tab.close();
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  const blocked = isPdf && !tab;   // a PDF that could not be shown because the tab was blocked: the caller keeps the URL for a fallback link
  // Otherwise navigation or the download has already started; release the URL once the browser has taken it
  if (!blocked) window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return blocked;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Documents Abby has shared with this partner: the partner agreement and any amendments.
 * Files are served through short-lived download links; nothing is cached in the browser.
 */
export function AgreementPage() {
  const [documents, setDocuments] = useState<PartnerDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [fallback, setFallback] = useState<{ id: string; url: string } | null>(null);
  useEffect(() => () => { if (fallback) URL.revokeObjectURL(fallback.url); }, [fallback]);

  useEffect(() => {
    api
      .getDocuments()
      .then((data) => setDocuments(data.documents))
      .catch((e) => setError((e as ApiError).error || 'Could not load your documents'))
      .finally(() => setIsLoading(false));
  }, []);

  // The tab is opened during the click (inside the browser's user-activation window), the file is fetched with
  // the session token, and the tab is pointed at the result. If the browser blocked the tab, a plain link is shown.
  const open = async (doc: PartnerDocument) => {
    setDownloadingId(doc.id);
    setError(null);
    setFallback(null);
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const { url, fileName, mimeType } = await api.fetchOwnDocumentObjectUrl(doc.id);
      if (deliverDocument(tab, url, fileName, mimeType)) setFallback({ id: doc.id, url });
    } catch (e) {
      if (tab) tab.close();
      setError((e as ApiError).error || 'Could not open the document');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Agreement</h1>
          <p className="text-gray-600 mt-1">Your partner agreement with Abby and any amendments to it.</p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Documents</CardTitle>
            <CardDescription>Documents open in a new tab. PDFs display in the browser; Word files download.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading...
              </div>
            ) : documents.length === 0 ? (
              <p className="text-sm text-gray-600">
                Your agreement will appear here once Abby has uploaded it. If you expected to see it, contact your Abby support
                representative.
              </p>
            ) : (
              <ul className="divide-y">
                {documents.map((doc) => (
                  <li key={doc.id} className="py-3 flex items-center justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <FileText className="h-5 w-5 text-abby-primary flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{doc.title}</p>
                        <p className="text-xs text-gray-500">
                          {doc.file_name} · {formatFileSize(doc.file_size)} · added {formatDate(doc.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {fallback?.id === doc.id && (
                        <a href={fallback.url} target="_blank" rel="noopener noreferrer" className="text-sm text-abby-primary underline">
                          Click to open
                        </a>
                      )}
                      <Button type="button" variant="outline" size="sm" onClick={() => open(doc)} disabled={downloadingId === doc.id}>
                        {downloadingId === doc.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                        Open
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
